import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, getDoc, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { useNavigate } from 'react-router-dom';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    role: ''
  });
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        // Get current user's role from Firestore
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCurrentUserRole(userData.role);
          
          // If not admin, redirect to dashboard
          if (userData.role !== 'admin') {
            navigate('/');
            return;
          }
        } else {
          // If user document doesn't exist, redirect
          navigate('/');
          return;
        }
        
        // If admin, fetch users
        fetchUsers();
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/');
      }
    };

    const fetchUsers = async () => {
      try {
        const usersCollection = collection(db, 'users');
        const userSnapshot = await getDocs(usersCollection);
        const userList = userSnapshot.docs.map(doc => {
          const userData = doc.data();
          return {
            id: doc.id,
            ...userData
          };
        });
        setUsers(userList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching users:', error);
        setLoading(false);
      }
    };

    checkAdminAccess();
  }, [navigate]);

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setFormData({
      displayName: user.displayName || '',
      email: user.email,
      role: user.role
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
   };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      const userRef = doc(db, 'users', editingUser);
      
      const updateData = { ...formData };
      
      // Update the user document
      await updateDoc(userRef, updateData);
      
      // Update local state
      setUsers(users.map(user => 
        user.id === editingUser ? { ...user, ...updateData } : user
      ));
      
      setEditingUser(null);
      setLoading(false);
      setSuccess('User updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error updating user:', error);
      setLoading(false);
      
      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        setError('You do not have permission to update this user.');
      } else if (error.code === 'not-found') {
        setError('User not found. They may have been deleted.');
      } else {
        setError(`Failed to update user: ${error.message}`);
      }
    }
  };

  const handleDelete = async (userId) => {
    const userToDelete = users.find(u => u.id === userId);
    if (!userToDelete) return;

    const confirmMessage = `Delete ${userToDelete.displayName || userToDelete.email} and ALL their data?\n\nThis will permanently remove:\n• User account\n• All invoices\n• All receipts\n• All clients\n\nThis cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      setLoading(true);
      setError(null);

      try {
        // Delete all user's invoices
        const invoicesQuery = query(collection(db, 'invoices'), where('userId', '==', userId));
        const invoicesSnap = await getDocs(invoicesQuery);
        const invoiceDeletes = invoicesSnap.docs.map(d => deleteDoc(doc(db, 'invoices', d.id)));
        await Promise.all(invoiceDeletes);

        // Delete all user's receipts
        const receiptsQuery = query(collection(db, 'receipts'), where('userId', '==', userId));
        const receiptsSnap = await getDocs(receiptsQuery);
        const receiptDeletes = receiptsSnap.docs.map(d => deleteDoc(doc(db, 'receipts', d.id)));
        await Promise.all(receiptDeletes);

        // Delete all user's clients
        const clientsQuery = query(collection(db, 'clients'), where('userId', '==', userId));
        const clientsSnap = await getDocs(clientsQuery);
        const clientDeletes = clientsSnap.docs.map(d => deleteDoc(doc(db, 'clients', d.id)));
        await Promise.all(clientDeletes);

        // Delete the user document
        await deleteDoc(doc(db, 'users', userId));

        // Update local state
        setUsers(users.filter(user => user.id !== userId));
        setSuccess(`Deleted ${userToDelete.displayName || userToDelete.email} and all their data.`);
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error('Error cascade deleting user:', error);
        setError(`Failed to delete user: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) return <div className="user-management-container">Loading...</div>;

  // If not admin, don't render the page (extra security)
  if (currentUserRole !== 'admin') return null;

  return (
    <div className="user-management-container">
      <h1 className="user-management-title">User Management</h1>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {success && (
        <div className="success-message">
          {success}
        </div>
      )}
      
      <div className="user-table-container">
        <table className="user-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td data-label="Name">
                  {editingUser === user.id ? (
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      className="form-input"
                    />
                  ) : (
                    user.displayName || 'N/A'
                  )}
                </td>
                <td data-label="Email">
                  {editingUser === user.id ? (
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                    />
                  ) : (
                    user.email
                  )}
                </td>
                <td data-label="Role">
                  {editingUser === user.id ? (
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  ) : (
                    <span className={`user-role-badge ${
                      user.role === 'admin' ? 'role-admin' : 'role-user'
                    }`}>
                      {user.role}
                    </span>
                  )}
                </td>
                <td className="responsive-actions-cell" data-label="Actions">
                  <div className="user-actions">
                    {editingUser === user.id ? (
                      <button 
                        onClick={handleSubmit}
                        className="user-action-btn user-save-btn"
                      >
                        Save
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleEdit(user)}
                        className="user-action-btn user-edit-btn"
                      >
                        Edit
                      </button>
                    )}
                    <button 
                      onClick={() => handleDelete(user.id)}
                      className="user-action-btn user-delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
